import { defineStore } from 'pinia';
import { ref } from 'vue';
import http, { csrf } from '@/api/http';

export interface AdminUser {
    id: number;
    name: string;
    email: string;
    two_factor_enabled: boolean;
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref<AdminUser | null>(null);
    const loading = ref(false);

    async function fetchUser() {
        const { data } = await http.get('/api/admin/me');
        // Validate the response shape. Was the root cause of the
        // "admin enters directly but shows no data" bug 2026-07-07:
        // when CORS blocked cookies or the SPA fallback served
        // /index.html, axios received a truthy HTML string that the
        // store treated as a valid user object → router beforeEach let
        // the request through → Dashboard rendered blank. Now we
        // require the exact shape the backend returns; anything else
        // is treated as a failed lookup and the beforeEach redirects
        // to /login.
        if (!data || typeof data !== 'object' || typeof data.id !== 'number' || typeof data.email !== 'string') {
            user.value = null;
            throw new Error('Invalid /me response — expected AdminUser payload');
        }
        user.value = data as AdminUser;
    }

    async function login(email: string, password: string, code?: string) {
        loading.value = true;
        try {
            await csrf();
            await http.post('/api/admin/login', { email, password, code });
            await fetchUser();
        } finally {
            loading.value = false;
        }
    }

    async function logout() {
        await http.post('/api/admin/logout');
        user.value = null;
    }

    return { user, loading, fetchUser, login, logout };
});
